import React, { VFC, useState, useCallback, useMemo, useEffect } from 'react';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { AxiosError } from 'axios';
import { KeyedMutator } from 'swr';
import {
  Box,
  makeStyles,
  Theme,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
} from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { useCorporateCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { GenericDialog } from '../../../../../content-composer/src/components/Generic/GenericDialog/GenericDialog';
import { DialogAction } from '../../../../../content-composer/src/components/Generic/types';
import { ECOMMBXLoader } from '../../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { NewFavouriteAppTransactionBody } from '../../../domain/FavouriteTransfer/entities/NewFavouriteAppTransactionResponseDTO';

export interface SaveTemplateDialogProps {
  error: AxiosError<APIGatewayResponse<NewFavouriteAppTransactionBody>, any> | undefined;
  isLoading: boolean;
  isOpen: boolean;
  onToggle: Function;
  mutateFn?: KeyedMutator<NewFavouriteAppTransactionBody>;
  shouldSaveTransferAsFavourite: boolean;
  setShouldSaveTransferAsFavorite: React.Dispatch<React.SetStateAction<boolean>>;
  setFavouriteTransferPostData: React.Dispatch<React.SetStateAction<null | { name: string; isShared: boolean }>>;
  createNewFavoriteTransactionData?: NewFavouriteAppTransactionBody;
  onToggleTemplateSaveBtn: () => void;
}

const useStyles = makeStyles<Theme>(({ palette, spacing, breakpoints }) => ({
  dialogContent: {
    marginBottom: spacing(5),
  },
  dialogTitle: {
    color: palette.primary.main,
    '& .MuiTypography-root': {
      fontSize: '16px',
    },
  },
  loaderContainer: {
    width: '100%',
    marginTop: spacing(4),
    display: 'flex',
    justifyContent: 'center',
    [breakpoints.down('sm')]: {
      flex: 1,
      alignItems: 'center',
    },
  },
  successText: {
    color: palette.success.main,
  },
}));

const SaveTemplateDialog: VFC<SaveTemplateDialogProps> = ({
  createNewFavoriteTransactionData,
  error,
  isLoading,
  isOpen,
  onToggle,
  mutateFn,
  shouldSaveTransferAsFavourite,
  setShouldSaveTransferAsFavorite,
  setFavouriteTransferPostData,
  onToggleTemplateSaveBtn,
}) => {
  const corporateId = useCorporateCustomerId();
  const isCorporate = Boolean(corporateId);
  const [template, setTemplate] = useState<string>('');
  const [isShared, setIsShared] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { t } = useTranslation();

  const postDataFieldError = error?.response?.data.headers[0].param;
  const headerErrors =
    error?.response?.data.headers[0].param === 'name' ? error?.response?.data.headers[0].message : undefined;

  const onCloseTemplate = useCallback(() => {
    onToggle();
    setIsShared(false);
    setShouldSaveTransferAsFavorite(false);
    setTemplate('');
    setFavouriteTransferPostData(null);
  }, [onToggle, setTemplate, setShouldSaveTransferAsFavorite, setFavouriteTransferPostData]);

  const onChange = useCallback((event: any) => {
    setHasError(event.target.value.length > 40);
    setTemplate(event.target.value);
  }, []);
  const handleSwitchChange = () => setIsShared(prev => !prev);

  const onSaveFavouriteTransfer = useCallback(() => {
    if (shouldSaveTransferAsFavourite) {
      setFavouriteTransferPostData({ name: template, isShared });
      mutateFn && mutateFn();
      return;
    }
    setFavouriteTransferPostData({ name: template, isShared });
    setShouldSaveTransferAsFavorite(true);
  }, [
    shouldSaveTransferAsFavourite,
    mutateFn,
    setShouldSaveTransferAsFavorite,
    setFavouriteTransferPostData,
    template,
    isShared,
  ]);

  // memoize
  const dialogActionsConfig: DialogAction[] = useMemo(
    () => [
      {
        onClick: onCloseTemplate,
        label: t('fundsTransfer.SuccessStep.Dialog.Button.Cancel'),
        color: 'primary',
        variant: 'outlined',
        disabled: isLoading || Boolean(createNewFavoriteTransactionData),
      },
      {
        onClick: onSaveFavouriteTransfer,
        label: t('fundsTransfer.SuccessStep.Dialog.Button.Submit'),
        color: 'primary',
        variant: 'contained',
        disabled: isLoading || Boolean(createNewFavoriteTransactionData) || hasError,
      },
    ],
    [t, onCloseTemplate, isLoading, onSaveFavouriteTransfer, createNewFavoriteTransactionData, hasError],
  );

  useEffect(() => {
    if (createNewFavoriteTransactionData) {
      const timer = setTimeout(() => onCloseTemplate(), 2000);
      onToggleTemplateSaveBtn();
      return () => clearTimeout(timer);
    }
  }, [createNewFavoriteTransactionData, onCloseTemplate]);

  const classes = useStyles();

  return (
    <GenericDialog
      fullwidth
      dialogTitle={t('fundsTransfer.SuccessStep.Dialog.Title')}
      error={postDataFieldError ? undefined : error?.message}
      isError={!postDataFieldError && Boolean(error)}
      isDialogOpen={isOpen}
      onToggleDialog={onCloseTemplate}
      dialogActions={dialogActionsConfig}
      dialogTitleClassName={classes.dialogTitle}
      dialogContentClassName={classes.dialogContent}
      stickDrawerTitleToLeft={true}
      disableMobileDrawerHeight={true}
      isConfirmationResponsiveDialog={true}
      dialogContent={
        createNewFavoriteTransactionData ? (
          <Box mb={3} mt={3} display='flex' justifyContent='center'>
            <Typography className={classes.successText} variant='h5'>
              {t('fundsTransfer.SuccessStep.Dialog.SuccessMessage')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={3}>
              <Typography color='textSecondary' variant='body2'>
                {t('fundsTransfer.SuccessStep.Dialog.Subtitle')}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label={t('fundsTransfer.SuccessStep.Dialog.Content.Label')}
              placeholder={t('fundsTransfer.SuccessStep.Dialog.Content.Placeholder')}
              name='alias'
              variant='filled'
              value={template}
              disabled={isLoading}
              onChange={onChange}
              error={!!postDataFieldError || hasError}
              helperText={hasError ? t('fundsTransfer.SuccessStep.Dialog.Alias.ErrorMessage') : headerErrors}
            />
            {isCorporate && (
              <Box pl={1} pt={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isShared}
                      onChange={handleSwitchChange}
                      name='Share'
                      color='primary'
                      disabled={isLoading || Boolean(createNewFavoriteTransactionData)}
                    />
                  }
                  label={t('fundsTransfer.SuccessStep.Switch.Label')}
                />
              </Box>
            )}
            {isLoading && (
              <Box className={classes.loaderContainer}>
                <ECOMMBXLoader size='small' verticalSpacing='small' />
              </Box>
            )}
          </>
        )
      }
      isLoading={false}
      isSuccess={false}
    />
  );
};

export { SaveTemplateDialog };
